// 任务基类
import { TASK_ACTIVITY_MAP, ACTIVITY_CACHE_TTL_MS } from "./activityGate.js"
import { getLocalDayKey } from '../utils/dailyRuntime.js'
import { TaskRecoveryLedger } from './taskRecoveryLedger.js'
export class TaskBase {
  static _runningKeys = new Map()
  static _globalTimeoutHandles = new Map()
  static _delayPlans = new Map()
  static _queuedKeys = new Set()
  static _activityInfoCache = new Map()

  // 登录相关：账号级互斥锁链、连续失败计数、登录有效短期缓存
  static _loginChain = new Map()           // accountKey -> Promise（串行化同一账号的重登）
  static _loginFailCount = new Map()       // accountKey -> 连续失败次数（用于指数退避）
  static _loginStatusCache = new Map()     // accountKey -> { valid, at }（有效判定 60s 缓存）

  // 活动未开启当日节流
  static _activityClosedDays = new Map()   // (taskId, accountKey) -> day
  static _activityClosedLogged = new Set() // (taskId, accountKey, day)

  // 登录缓存与重登退避参数
  static LOGIN_STATUS_CACHE_TTL_MS = 60 * 1000
  static LOGIN_BACKOFF_BASE_MS = 1000
  static LOGIN_BACKOFF_MAX_MS = 30 * 1000

  static cancelTimeoutExecute(taskId, accountKey) {
    const timeoutKey = `${taskId}:${accountKey}`
    const existing = TaskBase._globalTimeoutHandles.get(timeoutKey)
    if (!existing) return false
    clearTimeout(existing)
    TaskBase._globalTimeoutHandles.delete(timeoutKey)
    TaskBase._delayPlans.delete(timeoutKey)
    TaskBase._queuedKeys.delete(timeoutKey)
    return true
  }

  static clearAllTimeoutExecutes() {
    const count = TaskBase._globalTimeoutHandles.size
    for (const handle of TaskBase._globalTimeoutHandles.values()) {
      clearTimeout(handle)
    }
    TaskBase._globalTimeoutHandles.clear()
    TaskBase._delayPlans.clear()
    TaskBase._queuedKeys.clear()
    return count
  }

  static resetDailyRuntimeState() {
    const cancelledTimers = this.clearAllTimeoutExecutes()
    this._runningKeys.clear()
    this._activityInfoCache.clear()
    this._activityClosedDays.clear()
    this._activityClosedLogged.clear()
    this._loginStatusCache.clear()
    this._loginFailCount.clear()
    this._loginChain.clear()
    return { cancelledTimers }
  }

  // 按账号串行化执行（同一账号的并发重登会排队，避免重登风暴）
  static withLoginLock(accountKey, task) {
    const prev = TaskBase._loginChain.get(accountKey) || Promise.resolve()
    const run = prev.then(task, task) // 无论上一次成败，本次任务都在其结束后执行
    TaskBase._loginChain.set(accountKey, run.then(() => undefined, () => undefined))
    return run
  }

  constructor(config) {
    this.id = config.id
    this.name = config.name
    this.description = config.description
    this.icon = config.icon || "⚙️"
    this.category = config.category || "general"
    this.defaultConfig = config.defaultConfig || {} 
    this.appStore = null // 将在TaskManager中设置
    // 账户级别的定时器句柄，确保同一任务同一账号最多只有一个
    this._timeoutHandles = new Map()
    this.taskManager = null
    // 存储每个账号的配置，格式：accountKey -> config
    this._accountConfigs = new Map()
    // 重试相关配置
    this.maxRetryCount = config.maxRetryCount || 10 // 最大重试次数，默认5次
    // 存储每个账号的重试计数
    this._retryCounters = new Map()
  }

  // 设置appStore引用
  setAppStore(appStore) {
    this.appStore = appStore
  }

  // 设置日志函数
  setLogFunction(logFunction) {
    this.logFunction = logFunction
  }

  // 添加日志的辅助方法
  addLog(message, type = "info") {
    if (this.logFunction) {
      this.logFunction(message, type)
    } else {
      console.log(`[${type.toUpperCase()}] ${message}`)
    }
  }

  setTaskManager(taskManager) {
    this.taskManager = taskManager
  }

  track(method, payload = {}) {
    const tracker = this.taskManager?.runtimeTracker
    if (tracker && typeof tracker[method] === "function") {
      tracker[method]({
        taskId: this.id,
        taskName: this.name,
        accountKey: payload.accountKey,
        ...payload
      })
    }
  }

  getExecutionGeneration() {
    return this.taskManager?.getExecutionGeneration?.() ?? 0
  }

  assertExecutionCurrent(generation) {
    if (this.taskManager?.isExecutionGenerationCurrent && !this.taskManager.isExecutionGenerationCurrent(generation)) {
      const error = new Error(this.taskManager.migrationPaused ? "任务已因迁移恢复停止" : this.taskManager.transportPaused ? "转发故障，任务已暂停" : "任务已被每日重置中断")
      error.code = "DAILY_RUNTIME_RESET"
      throw error
    }
  }

  isDailyRuntimeResetError(error) {
    return error?.code === "DAILY_RUNTIME_RESET"
  }

  waitWithExecutionCancellation(delay, generation = this.getExecutionGeneration()) {
    this.assertExecutionCurrent(generation)
    const signal = this.taskManager?.getExecutionAbortSignal?.()
    if (!signal) {
      return new Promise((resolve) => setTimeout(resolve, delay))
    }

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", onAbort)
        resolve()
      }, delay)
      const onAbort = () => {
        clearTimeout(timer)
        signal.removeEventListener("abort", onAbort)
        try {
          this.assertExecutionCurrent(generation)
        } catch (error) {
          reject(error)
          return
        }
        reject(new Error("任务等待已被取消"))
      }
      signal.addEventListener("abort", onAbort, { once: true })
    })
  }

  // 获取账号信息的辅助方法
  getAccountInfo(accountKey) {
    if (!this.appStore) {
      throw new Error("appStore未初始化")
    }
    
    const accounts = this.appStore.accounts
    const account = accounts[accountKey]
    
    if (!account) {
      throw new Error(`账号 ${accountKey} 不存在`)
    }
    
    return account
  }

  // 任务数据存储方法（持久化到localStorage）
  setTaskData(accountKey, data) {
    if (!this.appStore) {
      throw new Error("appStore未初始化")
    }
    this.appStore.setTaskData(this.id, accountKey, data)
  }

  getTaskData(accountKey) {
    if (!this.appStore) {
      throw new Error("appStore未初始化")
    }
    return this.appStore.getTaskData(this.id, accountKey)
  }

  clearTaskData(accountKey) {
    if (!this.appStore) {
      throw new Error("appStore未初始化")
    }
    this.appStore.clearTaskData(this.id, accountKey)
  }

  // 简单的API请求函数
  async simpleApiRequest(accountKey, controller, method, body = {}, extraParams = {}) {
    const executionGeneration = this.getExecutionGeneration()
    this.assertExecutionCurrent(executionGeneration)
    if (this.taskManager?.activeTaskRuns?.get(`${this.id}:${accountKey}`)?.stopped) {
      const error = new Error('任务已被用户停止')
      error.code = 'TASK_STOPPED'
      throw error
    }
    if (!this.appStore) {
      throw new Error("appStore未初始化")
    }

    const account = this.getAccountInfo(accountKey)
    const { apiService } = this.appStore

    if (!apiService) {
      throw new Error("ApiService未初始化")
    }

    // 构建基础参数
    const params = {
      c: controller,
      m: method,
      token: account.token,
      token_uid: account.tokenUid,
      ...extraParams
    }

    // status 11 = 操作过快（服务器限流）：在请求层做轻量指数退避重试（2s -> 4s，最多2次）
    // 若仍返回 11，则交回调用方决定是否抛出以触发任务级重试
    const RATE_LIMIT_STATUS = 11
    const RATE_LIMIT_RETRY_DELAYS = [2000, 4000]
    let result = null

    for (let attempt = 0; attempt <= RATE_LIMIT_RETRY_DELAYS.length; attempt++) {
      if (attempt > 0) {
        const delay = RATE_LIMIT_RETRY_DELAYS[attempt - 1]
        this.addLog(`[${accountKey}] 请求被限流（状态码11），${Math.round(delay / 1000)}秒后重试...`, "warning")
        await this.waitWithExecutionCancellation(delay, executionGeneration)
      }

      // 停止任务可能发生在限流退避期间；不能在用户停止后再发送下一次请求。
      if (this.taskManager?.activeTaskRuns?.get(`${this.id}:${accountKey}`)?.stopped) {
        const error = new Error('任务已被用户停止')
        error.code = 'TASK_STOPPED'
        throw error
      }

      const ledger = this.taskManager?.activeTaskRuns?.get(`${this.id}:${accountKey}`)?.ledger
      try {
        ledger?.beforeRequest(controller, method, body)
        result = await apiService.apiRequest(params, body, account.server)
        ledger?.afterRequest(controller, method, body, result)
      } catch (error) {
        ledger?.requestFailed()
        // 请求被午夜重置中止时，统一转换成任务取消，避免被当作普通网络错误安排重试。
        this.assertExecutionCurrent(executionGeneration)
        throw error
      }
      this.assertExecutionCurrent(executionGeneration)

      if (!(result && typeof result === "object" && Number(result.status) === RATE_LIMIT_STATUS)) {
        break
      }
    }

    // 活动未开启节流检测：接口明确返回"活动未开启"时，当天该活动不再重复触发
    if (result && typeof result === "object" && result.msg && String(result.msg).includes("活动未开启")) {
      this.markActivityClosedToday(accountKey)
    }

    if (!result || result == 403) {
      throw new Error(`API请求失败: ${result?.msg}`)
    }

    return result;
  }

  // 获取活跃度状态（按账号缓存，约 ACTIVITY_CACHE_TTL_MS 后过期，并随本地日期变化失效）
  async getActivityInfo(accountKey) {
    const today = new Date().toDateString()
    const cached = TaskBase._activityInfoCache.get(accountKey)
    if (cached && cached.day === today && Date.now() - cached.fetchedAt < ACTIVITY_CACHE_TTL_MS) {
      return cached.info
    }

    try {
      const result = await this.simpleApiRequest(accountKey, "activity", "index", {})
      const info = result && typeof result === "object" ? result.info : null
      if (info && typeof info === "object") {
        TaskBase._activityInfoCache.set(accountKey, {
          fetchedAt: Date.now(),
          day: today,
          info,
        })
        return info
      }
      return null
    } catch (error) {
      this.addLog(`[${accountKey}] 获取活跃度状态失败，跳过活跃度判定: ${error.message}`, "warning")
      return null
    }
  }

  // 判断指定活跃度任务是否已完成（info[key] === 2）
  async isActivityCompleted(accountKey, activityKey) {
    const info = await this.getActivityInfo(accountKey)
    if (!info || info[activityKey] === undefined) {
      return false
    }
    return String(info[activityKey]) === "2"
  }

  // 活跃度闸门开关（默认开启）
  isActivitySkipEnabled() {
    if (!this.appStore || !this.appStore.config) {
      return true
    }
    return this.appStore.config.activitySkipEnabled !== false
  }

  // 检查登录状态，如果失效则自动重新登录
  async checkLoginStatus(accountKey) {
    if (!this.appStore) {
      throw new Error("appStore未初始化")
    }
    const executionGeneration = this.getExecutionGeneration()
    this.assertExecutionCurrent(executionGeneration)

    const { apiService } = this.appStore

    if (!apiService) {
      throw new Error("ApiService未初始化")
    }

    // 登录有效判定短期缓存（60s），减少每个任务/每次重试重复调用 member/index
    const now = Date.now()
    const cachedValid = TaskBase._loginStatusCache.get(accountKey)
    if (cachedValid && cachedValid.valid && now - cachedValid.at < TaskBase.LOGIN_STATUS_CACHE_TTL_MS) {
      return true
    }

    const account = this.getAccountInfo(accountKey)
    try {
      const isValid = await apiService.checkLoginStatus(account.server, account.token)
      if (isValid) {
        TaskBase._loginStatusCache.set(accountKey, { valid: true, at: now })
        return true // token有效，继续执行
      }
    } catch (error) {
      // member/index 接口异常时仍尝试重登（重登内部有账号级互斥锁与退避兜底）
    }

    // token 失效：在账号级互斥锁内串行重登，避免并发重登风暴
    await TaskBase.withLoginLock(accountKey, async () => {
      const freshAccount = this.getAccountInfo(accountKey)

      // 锁内重查：可能已有其他调用完成了重登
      const stillValid = await apiService.checkLoginStatus(freshAccount.server, freshAccount.token)
      if (stillValid) {
        TaskBase._loginStatusCache.set(accountKey, { valid: true, at: Date.now() })
        return
      }

      this.addLog(`[${accountKey}] 账号 ${freshAccount.username} (${freshAccount.server}) token已失效，正在重新登录...`, "warning")

      // 同一账号连续失败采用指数退避，避免短时间打爆登录接口
      const failCount = TaskBase._loginFailCount.get(accountKey) || 0
      if (failCount > 0) {
        const backoffMs = Math.min(TaskBase.LOGIN_BACKOFF_MAX_MS, TaskBase.LOGIN_BACKOFF_BASE_MS * Math.pow(2, failCount - 1))
        this.addLog(`[${accountKey}] 账号 ${freshAccount.username} 重新登录失败 ${failCount} 次，退避 ${Math.round(backoffMs / 1000)}s 后重试`, "info")
        await this.waitWithExecutionCancellation(backoffMs, executionGeneration)
      }

      if (this.appStore.setLoginLock) {
        this.appStore.setLoginLock(accountKey, `relogin:${Date.now()}`)
      }
      try {
        const loginResult = await apiService.login(freshAccount.server, freshAccount.username, freshAccount.password)

        // 防御：必须拿到有效 uid 才视为重登成功，否则抛出清晰错误而非 TypeError
        if (loginResult.success && loginResult.userInfo && loginResult.userInfo.uid) {
          this.appStore.updateUserInfo(accountKey, loginResult.userInfo)
          this.appStore.updateAccountAuth(accountKey, { token: loginResult.token, tokenUid: loginResult.userInfo.uid })
          TaskBase._loginFailCount.delete(accountKey)
          TaskBase._loginStatusCache.set(accountKey, { valid: true, at: Date.now() })
          this.addLog(`[${accountKey}] 账号 ${freshAccount.username} (${freshAccount.server}) 重新登录成功`, "success")
          return
        }

        const reason = (loginResult && loginResult.error) || "用户信息缺失"
        TaskBase._loginFailCount.set(accountKey, failCount + 1)
        throw new Error(`重新登录失败: ${reason}`)
      } finally {
        if (this.appStore.setLoginLock) {
          this.appStore.setLoginLock(accountKey, null)
        }
      }
    })

    return true
  }

  // 致命类错误判断：登录/token 失效类错误重试不会立刻自愈，不应快速重试
  isFatalLoginError(error) {
    const message = String((error && error.message) || "")
    return message.includes("登录状态检查失败") || message.includes("重新登录失败")
  }

  // 活动未开启节流：接口明确返回“活动未开启”时记录当日状态
  markActivityClosedToday(accountKey) {
    TaskBase._activityClosedDays.set(`${this.id}:${accountKey}`, new Date().toDateString())
  }

  // 当日该活动是否已确认未开启
  isActivityClosedToday(accountKey) {
    return TaskBase._activityClosedDays.get(`${this.id}:${accountKey}`) === new Date().toDateString()
  }

  // 当天仅记录一次“活动未开启，今天不再重复执行”
  logActivityClosedSkipOnce(accountKey) {
    const logKey = `${this.id}:${accountKey}:${new Date().toDateString()}`
    if (TaskBase._activityClosedLogged.has(logKey)) return
    TaskBase._activityClosedLogged.add(logKey)
    this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 活动未开启，今天不再重复执行`, "info")
  }

  // 获取任务UI配置（子类必须实现）
  async getUIConfig() {
    return {
      controls: []
    }
  }

  // 执行任务（子类必须实现）
  // 注意：accountKey格式为 "username_S{server}"
  async main() {}

  async execute(accountKey, executionOptions = {}) {
    const executionKey = `${this.id}:${accountKey}`
    if (TaskBase._runningKeys.has(executionKey)) {
      this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 已在执行中，跳过本次重复触发`, "warning")
      this.track("skipTask", {
        accountKey,
        lastMessage: `任务 ${this.name} (${this.id}) 已在执行中，跳过本次重复触发`
      })
      return true
    }

    const executionToken = Symbol(executionKey)
    TaskBase._runningKeys.set(executionKey, executionToken)
    const run = this.taskManager?.startTaskRun(this, accountKey)
    // 获取当前重试计数，如果不存在则初始化为0
    let retryCount = this._retryCounters.get(accountKey) || 0
    const executionGeneration = this.getExecutionGeneration()

    try {
      while (retryCount <= this.maxRetryCount) {
        try {
          this.assertExecutionCurrent(executionGeneration)
          if (run?.ledger.recovering && run.ledger.cannotRestart()) {
            this.addLog(`[${accountKey}] ${run.ledger.restartSkipReason()}`, 'warning')
            this.track('skipTask', { accountKey, lastMessage: run.ledger.restartSkipReason() })
            return true
          }
          // 如果是重试，记录重试信息
          if (retryCount > 0) {
            this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 第${retryCount}次重试`, "info")
          }

          // 先执行登录状态检查
          try {
            await this.checkLoginStatus(accountKey)
          } catch (error) {
            this.assertExecutionCurrent(executionGeneration)
            throw error
          }
          this.assertExecutionCurrent(executionGeneration)

          // 活动未开启当日节流：已确认今日活动未开启则直接跳过，避免反复请求
          if (this.isActivityClosedToday(accountKey)) {
            this._retryCounters.delete(accountKey)
            this.logActivityClosedSkipOnce(accountKey)
            this.track("skipTask", {
              accountKey,
              lastMessage: "活动未开启，今天不再重复执行"
            })
            return true
          }

          // 活跃度闸门：对应活跃度任务已完成则跳过（覆盖批量/单个/定时/延迟重试等所有路径）
          const activityKey = TASK_ACTIVITY_MAP[this.id]
          if (activityKey && this.isActivitySkipEnabled() && (await this.isActivityCompleted(accountKey, activityKey))) {
            this._retryCounters.delete(accountKey)
            this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 的活跃度已完成，跳过执行`, "info")
            this.track("skipTask", {
              accountKey,
              lastMessage: "活跃度已完成，跳过执行"
            })
            return true
          }
          this.assertExecutionCurrent(executionGeneration)

          // 执行具体任务逻辑
          // 恢复原手动批次时不能再次获得提前执行权限。
          const result = await this.main(accountKey, {
            source: run?.ledger.recovering ? 'recovery' : executionOptions.source,
          })
          this.assertExecutionCurrent(executionGeneration)
          if (run?.stopped && result !== false) return { cancelled: true }

          // 任务成功完成，清除重试计数
          this._retryCounters.delete(accountKey)
          if (result && typeof result === "object" && result.deferred === true) {
            this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 本轮未执行操作，已安排后续检查`, "info")
            return false
          }
          this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) ${result === false ? '本轮未完成，请查看前面的异常日志' : '执行成功'}`, result === false ? 'warning' : 'success')
          return result

        } catch (error) {
          if (error.code === 'TASK_STOPPED') return { cancelled: true }
          if (this.isDailyRuntimeResetError(error)) {
            this._retryCounters.delete(accountKey)
            this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) ${this.taskManager?.migrationPaused ? '已因迁移恢复停止' : this.taskManager?.transportPaused ? '已因转发故障暂停' : '已被每日凌晨重置中断'}`, "info")
            return { cancelled: true }
          }
          if (error.retryable === false) {
            this.taskManager?.pauseForTransport?.()
            this._retryCounters.delete(accountKey)
            this.addLog(error.message, 'warning')
            return { cancelled: true }
          }
          retryCount++
          this._retryCounters.set(accountKey, retryCount)

          // 记录错误日志
          this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 执行失败: ${error.message}`, "error")

          // 致命类（登录/token 失效）：不做快速重试，直接进入5分钟延迟重试，避免无效连打
          if (this.isFatalLoginError(error)) {
            this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 登录状态异常，跳过快速重试，将在5分钟后重试`, "warning")
            this._retryCounters.delete(accountKey)
            this.setTimeoutExecute(accountKey, 5 * 60 * 1000)
            throw error
          }

          // 如果已达到最大重试次数，放弃重试并设置超时
          if (retryCount > this.maxRetryCount) {
            this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 已达到最大重试次数(${this.maxRetryCount})，放弃重试`, "error")
            // 清除重试计数
            this._retryCounters.delete(accountKey)
            // 设置超时重试（保留原有的超时机制作为最后手段）
            this.setTimeoutExecute(accountKey, 5 * 60 * 1000)
            throw error
          }

          // 瞬时类失败：指数退避后重试（1s/2s/4s...上限30s）
          const backoffMs = Math.min(30 * 1000, 1000 * Math.pow(2, retryCount - 1))
          this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 将在 ${Math.round(backoffMs / 1000)}s 后重试`, "info")
          await new Promise(resolve => setTimeout(resolve, backoffMs))
        }
      }
    } finally {
      this.taskManager?.finishTaskRun(executionKey, run)
      if (TaskBase._runningKeys.get(executionKey) === executionToken) {
        TaskBase._runningKeys.delete(executionKey)
      }
    }
  }

  setTimeoutExecute(accountKey, delay) {
    if (this.taskManager?.migrationPaused || this.taskManager?.transportPaused) return
    if (this.taskManager?.activeTaskRuns?.get(`${this.id}:${accountKey}`)?.stopped) return
    const timeoutKey = `${this.id}:${accountKey}`
    const executionGeneration = this.getExecutionGeneration()
    // 若已有定时器，先清除，确保同一任务同一账号只有一个定时任务
    const existing = TaskBase._globalTimeoutHandles.get(timeoutKey)
    if (existing) {
      clearTimeout(existing)
      this.track("cancelDelay", {
        id: `delay:${this.id}:${accountKey}`,
        accountKey,
        lastMessage: "新的延迟任务已替换旧任务"
      })
    }

    this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 将在${parseInt(delay / 60000)}分钟后重试`, "info")
    const runAfter = new Date(Date.now() + delay).toISOString()
    this.track("scheduleDelay", {
      id: `delay:${this.id}:${accountKey}`,
      accountKey,
      runAfter,
      lastMessage: `将在 ${parseInt(delay / 60000)} 分钟后重试`
    })
    const handle = setTimeout(() => {
      // 触发后移除句柄，避免残留
      TaskBase._globalTimeoutHandles.delete(timeoutKey)
      TaskBase._delayPlans.delete(timeoutKey)
      if (this.taskManager?.isExecutionGenerationCurrent && !this.taskManager.isExecutionGenerationCurrent(executionGeneration)) {
        return
      }
      this.track("triggerDelay", {
        id: `delay:${this.id}:${accountKey}`,
        accountKey
      })
      // 将定时任务加入全局队列，确保串行执行
      if (TaskBase._queuedKeys.has(timeoutKey)) {
        this.addLog(`[${accountKey}] 任务 ${this.name} (${this.id}) 已在队列中，跳过本次重复触发`, "warning")
        this.track("skipTask", {
          id: `delay:${this.id}:${accountKey}`,
          accountKey,
          lastMessage: "延迟任务已在队列中，跳过重复触发"
        })
        return
      }
      TaskBase._queuedKeys.add(timeoutKey)
      this.taskManager.addToQueue({
        taskId: this.id,
        accountKey: accountKey,
        type: 'delayed',
        runtimeRecordId: `delay:${this.id}:${accountKey}`
      })
    }, delay)
    TaskBase._globalTimeoutHandles.set(timeoutKey, handle)
    TaskBase._delayPlans.set(timeoutKey, { taskId: this.id, accountKey, dueAt: Date.now() + delay, day: getLocalDayKey() })
  }

  // 更新配置
  updateConfig(accountKey, newConfig) {
    // 合并默认配置和新配置
    const mergedConfig = { ...this.defaultConfig, ...newConfig }
    
    // 存储到账号级别的配置中
    this._accountConfigs.set(accountKey, mergedConfig)
    
    console.log(`更新任务 ${this.id} 账号 ${accountKey} 的配置:`, mergedConfig)
  }

  // 获取指定账号的配置
  getAccountConfig(accountKey) {
    // 优先返回账号级别的配置，如果没有则返回默认配置
    const config = this._accountConfigs.get(accountKey) || { ...this.defaultConfig }
    return this.taskManager?.activeTaskRuns?.get(`${this.id}:${accountKey}`)?.ledger.configForAttempt(config) || config
  }

  // 清除指定账号的配置
  clearAccountConfig(accountKey) {
    this._accountConfigs.delete(accountKey)
    console.log(`清除任务 ${this.id} 账号 ${accountKey} 的配置`)
  }

  // 清除所有账号的配置
  clearAllAccountConfigs() {
    this._accountConfigs.clear()
    console.log(`清除任务 ${this.id} 的所有账号配置`)
  }
}

export class TaskManager {
  constructor() {
    this.tasks = new Map()
    this.categories = new Map()
    this.initialized = false
    this.appStore = null
    this.logFunction = null
    // 全局任务队列，确保所有任务（包括定时任务）串行执行
    this.taskQueue = []
    this.isProcessingQueue = false
    // 全局执行状态管理
    this.globalExecutionState = {
      isExecuting: false,
      currentBatch: null,
      executionId: null
    }
    // 事件监听器
    this.eventListeners = new Map()
    this.runtimeTracker = null
    this.executionGeneration = 0
    this.executionAbortController = new AbortController()
    this.activeTaskRuns = new Map()
    this.recoveryLedgers = new Map()
    this.transportCheckpoint = null
    this.currentQueueItem = null
  }

  getExecutionGeneration() {
    return this.executionGeneration
  }

  isExecutionGenerationCurrent(generation) {
    return !this.migrationPaused && !this.transportPaused && generation === this.executionGeneration
  }

  getExecutionAbortSignal() {
    return this.executionAbortController.signal
  }

  resetDailyRuntime() {
    this.discardTransportCheckpoint()
    const queuedCount = this.taskQueue.length
    this.executionGeneration++
    this.executionAbortController.abort()
    this.executionAbortController = new AbortController()
    this.taskQueue = []
    const { cancelledTimers } = TaskBase.resetDailyRuntimeState()
    this.tasks.forEach((task) => task._retryCounters?.clear())
    this.appStore?.apiService?.abortActiveRequests?.()
    this.globalExecutionState = {
      isExecuting: false,
      currentBatch: null,
      executionId: null
    }
    return { queuedCount, cancelledTimers, generation: this.executionGeneration }
  }

  pauseForMigration() {
    this.migrationPaused = true
    this.emitEvent('migrationPaused', {})
    if (this.appStore?.apiService) this.appStore.apiService.migrationPaused = true
    return this.resetDailyRuntime()
  }

  pauseForTransport() {
    if (this.transportPaused) return
    this.transportPaused = true
    const batch = this.globalExecutionState.currentBatch
    // 网络暂停不清除每日状态、购买记录及任务进度。
    const items = [...this.taskQueue]
    if (this.currentQueueItem) items.unshift(this.currentQueueItem)
    const covered = new Set(items.flatMap(item => this.getPendingPairs(item).map(pair => `${pair.taskId}:${pair.accountKey}`)))
    for (const [key, run] of this.activeTaskRuns) {
      if (run.stopped) continue
      this.recoveryLedgers.set(key, run.ledger)
      if (!covered.has(key)) items.unshift({ taskId: run.task.id, accountKey: run.accountKey, type: 'recovery', timestamp: Date.now() })
    }
    this.transportCheckpoint = { day: getLocalDayKey(), items, delays: [...TaskBase._delayPlans.values()] }
    this.taskQueue = []
    this.executionGeneration++
    this.executionAbortController.abort()
    this.executionAbortController = new AbortController()
    TaskBase.clearAllTimeoutExecutes()
    this.appStore?.apiService?.abortActiveRequests?.()
    this.globalExecutionState = { isExecuting: false, currentBatch: null, executionId: null }
    this.emitEvent('executionPaused', { reason: '转发故障，执行已暂停', executionId: batch?.executionId })
    return this.transportCheckpoint
  }

  startTaskRun(task, accountKey) {
    const key = `${task.id}:${accountKey}`
    const ledger = this.recoveryLedgers.get(key) || new TaskRecoveryLedger(task)
    this.recoveryLedgers.delete(key)
    let resolve
    const done = new Promise(finish => { resolve = finish })
    const run = { task, accountKey, ledger, done, resolve }
    this.activeTaskRuns.set(key, run)
    return run
  }

  finishTaskRun(key, run) {
    if (!run) return
    if (this.activeTaskRuns.get(key) === run) this.activeTaskRuns.delete(key)
    run.resolve()
  }

  async waitForTransportIdle() {
    await Promise.all([...this.activeTaskRuns.values()].map(run => run.done))
    if (this.queueIdle) await this.queueIdle
  }

  getPendingPairs(item) {
    if (item.type !== 'batch') return [item]
    return item.accounts.flatMap(accountKey => item.tasks.filter(taskId =>
      !item.completedPairs?.has(`${taskId}:${accountKey}`)).map(taskId => ({ taskId, accountKey })))
  }

  discardTransportCheckpoint() {
    this.transportCheckpoint = null
    this.recoveryLedgers.clear()
  }

  resumeAfterTransport() {
    if (this.migrationPaused) return
    const checkpoint = this.transportCheckpoint
    this.transportCheckpoint = null
    this.transportPaused = false
    TaskBase._loginStatusCache.clear()
    TaskBase._activityInfoCache.clear()
    if (checkpoint?.day === getLocalDayKey()) {
      this.recoveredPairs = new Set([
        ...checkpoint.items.flatMap(item => [
          ...this.getPendingPairs(item).map(pair => `${pair.taskId}:${pair.accountKey}`), ...(item.completedPairs || []),
        ]),
        ...checkpoint.delays.map(plan => `${plan.taskId}:${plan.accountKey}`),
      ])
      for (const [key, ledger] of this.recoveryLedgers) {
        ledger.recovering = true
        if (ledger.cannotRestart()) {
          const [taskId, ...accountParts] = key.split(':')
          const accountKey = accountParts.join(':')
          this.removeCheckpointPair(checkpoint, taskId, accountKey)
          this.logFunction?.(`[${accountKey}] ${this.getTask(taskId)?.name || taskId}：${ledger.restartSkipReason()}`, 'warning')
          this.getTask(taskId)?.track('skipTask', { accountKey, lastMessage: ledger.restartSkipReason() })
          this.recoveryLedgers.delete(key)
        }
      }
      this.taskQueue = checkpoint.items
      const covered = new Set(this.taskQueue.flatMap(item => this.getPendingPairs(item).map(pair => `${pair.taskId}:${pair.accountKey}`)))
      for (const plan of checkpoint.delays) {
        if (plan.day !== getLocalDayKey() || covered.has(`${plan.taskId}:${plan.accountKey}`)) continue
        this.getTask(plan.taskId)?.setTimeoutExecute(plan.accountKey, Math.max(0, plan.dueAt - Date.now()))
      }
    } else {
      this.recoveredPairs = new Set()
      this.recoveryLedgers.clear()
    }
    this.restorePersistentTaskPlans()
    // 调用者先合并错过的定时计划，再启动队列。
  }

  removeCheckpointPair(checkpoint, taskId, accountKey) {
    checkpoint.items = checkpoint.items.filter(item => {
      if (item.type !== 'batch') return item.taskId !== taskId || item.accountKey !== accountKey
      item.completedPairs ??= new Set()
      item.completedPairs.add(`${taskId}:${accountKey}`)
      return true
    })
    checkpoint.delays = checkpoint.delays.filter(plan => plan.taskId !== taskId || plan.accountKey !== accountKey)
  }

  rescheduleDailyTaskPlans() {
    this.tasks.forEach((task) => task.rescheduleDailyPlans?.())
  }

  restorePersistentTaskPlans() {
    this.tasks.forEach((task) => task.restorePersistentPlans?.())
  }

  setRuntimeTracker(runtimeTracker) {
    this.runtimeTracker = runtimeTracker
  }

  track(method, payload = {}) {
    if (this.runtimeTracker && typeof this.runtimeTracker[method] === "function") {
      this.runtimeTracker[method](payload)
    }
  }

  // 设置appStore引用
  setAppStore(appStore) {
    this.appStore = appStore
    // 为所有已注册的任务设置appStore引用
    this.tasks.forEach(task => {
      task.setAppStore(appStore)
      if (this.logFunction) {
        task.setLogFunction(this.createTaskLogFunction(task))
      }
    })
  }

  setLogFunction(logFunction) {
    this.logFunction = typeof logFunction === 'function' ? logFunction : null
    this.tasks.forEach(task => {
      task.setLogFunction(this.createTaskLogFunction(task))
    })
  }

  createTaskLogFunction(task, baseMetadata = {}) {
    return (message, type = 'info', metadata = {}) => {
      const payload = {
        source: 'task',
        taskId: task.id,
        taskName: task.name,
        ...baseMetadata,
        ...metadata
      }
      if (this.logFunction) {
        this.logFunction(message, type, payload)
      } else {
        console.log(`[${type.toUpperCase()}] ${message}`)
      }
    }
  }

  // 初始化任务管理器
  async initialize() {
    if (this.initialized) return

    // 注册所有任务
    await this.registerAllTasks()
    this.initialized = true
  }

  // 注册所有任务
  async registerAllTasks() {
    try {
      // 动态导入所有任务
      const { ALL_TASKS } = await import('../tasks/index.js')
      
      // 注册所有任务
      ALL_TASKS.forEach(TaskClass => {
        try {
          const task = new TaskClass()
          this.register(task)
        } catch (error) {
          console.error(`注册任务失败: ${TaskClass.name}`, error)
        }
      })
      
      console.log(`成功注册 ${this.tasks.size} 个任务`)
    } catch (error) {
      console.error('加载任务模块失败:', error)
    }
  }

  // 注册任务
  register(task) {
    // 如果appStore已经设置，为任务设置appStore引用
    if (this.appStore) {
      task.setAppStore(this.appStore)
      
      // 同步默认配置到appStore
      this.syncTaskDefaultConfig(task)
    }
    task.setTaskManager(this);
    if (this.logFunction) {
      task.setLogFunction(this.createTaskLogFunction(task))
    }

    this.tasks.set(task.id, task)

    // 按分类组织
    if (!this.categories.has(task.category)) {
      this.categories.set(task.category, [])
    }
    this.categories.get(task.category).push(task)

    console.log(`任务 ${task.name} (${task.id}) 已注册`)
  }

  // 同步任务默认配置到appStore
  syncTaskDefaultConfig(task) {
    if (!this.appStore || !task.defaultConfig) {
      return
    }

    // 获取当前存储的配置
    const currentConfig = this.appStore.getGlobalTaskConfig(task.id) || {}
    
    // 合并配置：本地存储的配置优先级最高，然后是默认配置
    const mergedConfig = { ...task.defaultConfig, ...currentConfig }
    
    // 更新到appStore
    this.appStore.updateGlobalTaskConfig(task.id, mergedConfig)
    
    console.log(`任务 ${task.name} (${task.id}) 配置已同步到appStore`)
  }

  // 获取所有任务
  getAllTasks() {
    return Array.from(this.tasks.values())
  }

  // 获取任务
  getTask(taskId) {
    return this.tasks.get(taskId)
  }

  // 按分类获取任务
  getTasksByCategory(category) {
    return this.categories.get(category) || []
  }

  // 获取所有分类
  getCategories() {
    return Array.from(this.categories.keys())
  }

  // 执行任务（简化版本，移除统计逻辑）
  async executeTask(taskId, accountKey, appStore, config = {}, executionOptions = {}) {
    const task = this.getTask(taskId)
    if (!task) {
      throw new Error(`任务 ${taskId} 不存在`)
    }

    try {
      // 确保任务有appStore引用
      if (!task.appStore) {
        task.setAppStore(appStore)
      }
      
      // 应用配置
      if (Object.keys(config).length > 0) {
        task.updateConfig(accountKey, config)
      }
      
      // 使用带重试机制的任务执行方法
      const result = await task.execute(accountKey, executionOptions)
      
      return { success: result }
    } catch (error) {
      // 错误日志已经在execute中记录，这里只记录到console
      console.log(`任务 ${task.name} 执行异常: ${error.message}`)
      throw error
    }
  }

  // 添加任务到队列（支持批量任务和单个任务）
  addToQueue(taskItem) {
    if (this.migrationPaused || this.transportPaused) return
    // 为任务项添加时间戳，确保按时间顺序执行
    taskItem.timestamp = Date.now()
    const task = this.getTask(taskItem.taskId)
    this.track("startTask", {
      id: taskItem.runtimeRecordId || `queue:${taskItem.taskId}:${taskItem.accountKey}:${taskItem.timestamp}`,
      executionId: taskItem.executionId,
      source: taskItem.type || "queue",
      taskId: taskItem.taskId,
      taskName: task?.name || taskItem.taskId,
      accountKey: taskItem.accountKey,
      status: "queued",
      lastMessage: "任务已加入队列"
    })
    this.taskQueue.push(taskItem)
    this.processQueue()
  }

  // 添加批量任务到队列
  addBatchToQueue(batchItem) {
    if (this.migrationPaused || this.transportPaused) return
    batchItem.timestamp = Date.now()
    batchItem.type = 'batch'
    this.track("queueBatch", {
      executionId: batchItem.executionId,
      source: batchItem.source || "manual",
      scheduledJobId: batchItem.scheduledJobId,
      tasks: batchItem.tasks || [],
      accounts: batchItem.accounts || [],
      total: (batchItem.tasks || []).length * (batchItem.accounts || []).length,
      queuedAt: new Date(batchItem.timestamp).toISOString(),
      lastMessage: "批量任务已加入队列"
    })
    this.taskQueue.push(batchItem)
    this.processQueue()
  }

  // 处理任务队列
  async processQueue() {
    if (this.transportPaused || this.migrationPaused || this.isProcessingQueue || this.taskQueue.length === 0) {
      return
    }

    const processingGeneration = this.executionGeneration
    this.isProcessingQueue = true
    let resolveIdle
    this.queueIdle = new Promise(resolve => { resolveIdle = resolve })
    this.globalExecutionState.isExecuting = true

    try {
      while (this.taskQueue.length > 0) {
        // 按时间戳排序，确保按时间顺序执行
        this.taskQueue.sort((a, b) => a.timestamp - b.timestamp)
        
        const taskItem = this.taskQueue.shift()
        this.currentQueueItem = taskItem
        const executionGeneration = this.executionGeneration
        
        if (taskItem.type === 'batch') {
          // 执行批量任务
          await this.executeBatch(taskItem)
        } else {
          // 执行单个任务
          await this.executeSingleTask(taskItem)
        }

        if (!this.isExecutionGenerationCurrent(executionGeneration)) {
          break
        }
      }
    } finally {
      this.currentQueueItem = null
      this.isProcessingQueue = false
      resolveIdle()
      if (this.isExecutionGenerationCurrent(processingGeneration)) {
        this.globalExecutionState.isExecuting = false

        // 触发执行完成事件
        this.emitEvent('executionComplete', {
          executionId: this.globalExecutionState.executionId,
          batch: this.globalExecutionState.currentBatch
        })

        this.globalExecutionState.currentBatch = null
        this.globalExecutionState.executionId = null
      }

      // 跨日时已有新计划入队：旧代次退出后再由新代次接手，不能让队列遗留。
      if (this.taskQueue.length > 0) {
        this.processQueue()
      }
    }
  }

  // 执行单个任务
  async executeSingleTask(taskItem) {
    const task = this.getTask(taskItem.taskId)
    
    if (task) {
      const executionGeneration = this.executionGeneration
      const queueKey = `${task.id}:${taskItem.accountKey}`
      const recordId = taskItem.runtimeRecordId || `queue:${taskItem.taskId}:${taskItem.accountKey}:${taskItem.timestamp}`
      const previousLogFunction = task.logFunction
      task.setLogFunction(this.createTaskLogFunction(task, {
        executionId: taskItem.executionId,
        source: taskItem.type || "queue",
        accountKey: taskItem.accountKey
      }))
      try {
        console.log(`[队列] 开始执行任务: ${task.name} - ${taskItem.accountKey}`)
        this.track("startTask", {
          id: recordId,
          executionId: taskItem.executionId,
          source: taskItem.type || "queue",
          taskId: task.id,
          taskName: task.name,
          accountKey: taskItem.accountKey,
          lastMessage: "队列任务开始执行"
        })
        const result = await task.execute(taskItem.accountKey, { source: taskItem.type || 'queue' })
        if (!this.isExecutionGenerationCurrent(executionGeneration) || result?.cancelled) return
        this.track("finishTask", {
          id: recordId,
          executionId: taskItem.executionId,
          source: taskItem.type || "queue",
          taskId: task.id,
          taskName: task.name,
          accountKey: taskItem.accountKey,
          lastMessage: "队列任务执行完成"
        })
      } catch (error) {
        if (!this.isExecutionGenerationCurrent(executionGeneration)) return
        console.log(`[队列] 任务执行失败: ${task.name} - ${taskItem.accountKey} - ${error.message}`)
        this.runtimeTracker?.failTask?.(error, {
          id: recordId,
          executionId: taskItem.executionId,
          source: taskItem.type || "queue",
          taskId: task.id,
          taskName: task.name,
          accountKey: taskItem.accountKey
        })
      } finally {
        task.setLogFunction(previousLogFunction || this.createTaskLogFunction(task))
        if (this.isExecutionGenerationCurrent(executionGeneration)) {
          TaskBase._queuedKeys.delete(queueKey)
        }
      }
    }
  }

  // 执行批量任务
  async executeBatch(batchItem) {
    const { tasks, accounts, configs, executionId, onProgress, onLog } = batchItem
    const requestInterval = Number(batchItem.requestInterval)
    const hasRequestIntervalOverride = Number.isFinite(requestInterval) && requestInterval >= 0
    const apiService = this.appStore?.apiService
    const previousRequestInterval = apiService?.requestInterval
    const executionGeneration = this.executionGeneration
    const createLogMetadata = (extra = {}) => ({
      executionId,
      source: batchItem.source || "manual",
      scheduledJobId: batchItem.scheduledJobId,
      ...extra
    })
    
    this.globalExecutionState.currentBatch = batchItem
    this.globalExecutionState.executionId = executionId
    
    // 触发执行开始事件
    this.emitEvent('executionStart', {
      executionId: executionId,
      batch: batchItem
    })
    
    try {
      if (hasRequestIntervalOverride && apiService) {
        apiService.requestInterval = Math.floor(requestInterval)
        if (onLog) onLog(
          `浏览器执行请求间隔已设置为 ${apiService.requestInterval}ms`,
          'info',
          createLogMetadata()
        )
      }

      if (onLog) onLog(
        `开始执行批量任务，共 ${tasks.length} 个任务，${accounts.length} 个账号`,
        'info',
        createLogMetadata()
      )
      
      const totalOperations = tasks.length * accounts.length
      batchItem.completedPairs ??= new Set()
      let completedOperations = batchItem.completedPairs.size
      this.track("startBatch", {
        executionId,
        source: batchItem.source || "manual",
        scheduledJobId: batchItem.scheduledJobId,
        tasks,
        accounts,
        total: totalOperations,
        completed: completedOperations,
        progress: totalOperations > 0 ? 0 : 100,
        lastMessage: `开始执行批量任务，共 ${tasks.length} 个任务，${accounts.length} 个账号`
      })

      // 串行执行：先遍历账号，再遍历任务，避免账号反复登录
      for (const accountKey of accounts) {
        if (!this.isExecutionGenerationCurrent(executionGeneration)) return
        if (onLog) onLog(`[${accountKey}] 开始执行所有任务`, 'info', createLogMetadata({ accountKey }))
        
        // 为当前账号执行所有选中的任务
        for (const taskId of tasks) {
          if (!this.isExecutionGenerationCurrent(executionGeneration)) return
          const pairKey = `${taskId}:${accountKey}`
          if (batchItem.completedPairs.has(pairKey)) continue
          const task = this.getTask(taskId)
          if (!task) {
            if (onLog) onLog(`任务 ${taskId} 不存在，跳过`, 'warning', createLogMetadata({ accountKey, taskId }))
            completedOperations++
            batchItem.completedPairs.add(pairKey)
            this.track("updateBatch", {
              executionId,
              completed: completedOperations,
              total: totalOperations,
              progress: totalOperations > 0 ? (completedOperations / totalOperations) * 100 : 100,
              lastMessage: `任务 ${taskId} 不存在，已跳过`
            })
            continue
          }

          // 使用配置快照
          const taskConfig = configs[taskId] || {}
          const previousLogFunction = task.logFunction

          try {
            const taskLogMetadata = createLogMetadata({
              accountKey,
              taskId: task.id,
              taskName: task.name
            })
            if (onLog) onLog(`[${accountKey}] 开始执行任务: ${task.name}`, 'info', taskLogMetadata)
            this.track("startTask", {
              executionId,
              source: batchItem.source || "manual",
              scheduledJobId: batchItem.scheduledJobId,
              taskId: task.id,
              taskName: task.name,
              accountKey,
              progress: 0,
              lastMessage: `开始执行任务: ${task.name}`
            })
            this.track("updateBatch", {
              executionId,
              currentTaskId: task.id,
              currentTaskName: task.name,
              currentAccountKey: accountKey,
              lastMessage: `[${accountKey}] 开始执行任务: ${task.name}`
            })
            
            // 为任务设置日志函数
            if (onLog) {
              task.setLogFunction((message, type = 'info', metadata = {}) => {
                onLog(message, type, { ...taskLogMetadata, ...metadata })
              })
            }
            
            // 更新任务配置
            if (Object.keys(taskConfig).length > 0) {
              task.updateConfig(accountKey, taskConfig)
            }

            // 执行任务
            const result = await task.execute(accountKey, { source: batchItem.source || 'manual' })
            if (!this.isExecutionGenerationCurrent(executionGeneration)) return
            if (result?.cancelled) {
              if (batchItem.completedPairs.has(pairKey)) { completedOperations++; continue }
              return
            }
            batchItem.completedPairs.add(pairKey)
            
            if (result) {
              if (onLog) onLog(`[${accountKey}] 任务 ${task.name} 执行完成`, 'success', taskLogMetadata)
            } else {
              if (onLog) onLog(`[${accountKey}] 任务 ${task.name} 执行完成`, 'info', taskLogMetadata)
            }

            completedOperations++
            if (onProgress) onProgress((completedOperations / totalOperations) * 100)
            this.track("finishTask", {
              executionId,
              source: batchItem.source || "manual",
              scheduledJobId: batchItem.scheduledJobId,
              taskId: task.id,
              taskName: task.name,
              accountKey,
              lastMessage: `任务 ${task.name} 执行完成`
            })
            this.track("updateBatch", {
              executionId,
              completed: completedOperations,
              total: totalOperations,
              progress: totalOperations > 0 ? (completedOperations / totalOperations) * 100 : 100,
              lastMessage: `[${accountKey}] 任务 ${task.name} 执行完成`
            })

            // 添加延迟避免请求过于频繁
            await task.waitWithExecutionCancellation(1000, executionGeneration)
            if (!this.isExecutionGenerationCurrent(executionGeneration)) return

          } catch (error) {
            if (!this.isExecutionGenerationCurrent(executionGeneration)) return
            batchItem.completedPairs.add(pairKey)
            if (onLog) {
              onLog(`[${accountKey}] 任务 ${task.name} 执行异常: ${error.message}`, 'error', createLogMetadata({
                accountKey,
                taskId: task.id,
                taskName: task.name
              }))
            }
            completedOperations++
            if (onProgress) onProgress((completedOperations / totalOperations) * 100)
            this.runtimeTracker?.failTask?.(error, {
              executionId,
              source: batchItem.source || "manual",
              scheduledJobId: batchItem.scheduledJobId,
              taskId: task.id,
              taskName: task.name,
              accountKey
            })
            this.track("updateBatch", {
              executionId,
              completed: completedOperations,
              total: totalOperations,
              progress: totalOperations > 0 ? (completedOperations / totalOperations) * 100 : 100,
              lastMessage: `[${accountKey}] 任务 ${task.name} 执行异常: ${error.message}`
            })
          } finally {
            task.setLogFunction(previousLogFunction || this.createTaskLogFunction(task))
          }
        }
        
        if (onLog) onLog(`[${accountKey}] 所有任务执行完成`, 'success', createLogMetadata({ accountKey }))
      }

      if (onLog) onLog(`批量任务执行完成`, 'success', createLogMetadata())
      this.track("finishBatch", {
        executionId,
        completed: totalOperations,
        total: totalOperations,
        progress: 100,
        lastMessage: "批量任务执行完成"
      })
    } catch (error) {
      if (onLog) onLog(`批量任务执行过程中发生错误: ${error.message}`, 'error', createLogMetadata())
      this.runtimeTracker?.failBatch?.(error, { executionId })
    } finally {
      if (hasRequestIntervalOverride && apiService && previousRequestInterval !== undefined) {
        apiService.requestInterval = previousRequestInterval
      }
    }
  }

  // 检查是否正在执行任务
  isExecuting() {
    return this.globalExecutionState.isExecuting
  }

  // 获取当前执行状态
  getExecutionState() {
    return { ...this.globalExecutionState }
  }

  // 添加事件监听器
  addEventListener(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event).push(callback)
  }

  // 移除事件监听器
  removeEventListener(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event)
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  // 触发事件
  emitEvent(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`事件监听器执行失败: ${event}`, error)
        }
      })
    }
  }

  // 停止任务（简化版本）
  stopTask(taskId, accountKey) {
    const task = this.getTask(taskId)
    if (!task) return false

    if (this.transportCheckpoint) this.removeCheckpointPair(this.transportCheckpoint, taskId, accountKey)
    const run = this.activeTaskRuns.get(`${taskId}:${accountKey}`)
    if (run) run.stopped = true
    const live = { items: [...this.taskQueue, ...(this.currentQueueItem ? [this.currentQueueItem] : [])], delays: [] }
    this.removeCheckpointPair(live, taskId, accountKey)
    this.taskQueue = live.items.filter(item => item !== this.currentQueueItem)
    this.recoveryLedgers.delete(`${taskId}:${accountKey}`)
    TaskBase.cancelTimeoutExecute(taskId, accountKey)

    task.cancelExecution?.(accountKey)
    console.log(`任务已停止: ${task.name}`)
    return true
  }
}
